"use client";

import { useEffect, useMemo, useState } from "react";
import { useHistoryManager } from "../history/useHistoryManager";
import { useQuery } from "@tanstack/react-query";
import { useClient, useToken } from "../../lib/context/auth-context";
import StoreBox from "../store/store-box";
import styles from "./index.module.css";
import Carousel from "../carousel/carousel";
import EmptyState from "../common/EmptyState";
import { buildQueryString, extractInfoFromUrl } from "../../lib/util";
import { useFormatCurrency } from "../common/format-currency";
import { useProductPriceDisplay } from "../product/useProductPriceDisplay";
import Skeleton from "react-loading-skeleton";
import Pagination from "../common/Pagination";
import { useAppDispatch } from "../slice/hooks";
import {
  setAccessPoint,
  setAccessPointId,
  setAccessPoints,
  setOrganisation,
} from "../slice/accessOrg-slice";
import { setActiveCategory, setCategories } from "../slice/category-slice";
import { useSwitchingOrgs } from "../organisation/useSwitchingOrgs";
import { closeIcon, rightArrowIcon } from "../../assets";
import { useModalManager } from "../modal/useModalManager";

import BreadcrumbMenu from "../common/BreadcrumbMenu";
import MiniCarousel from "../carousel/miniCarousel";
import MarketPlaceCategories from "./marketplaceCategories";
import ProductFilterPanel from "./productFilterPanel";
import Drawermenu from "../store/drawer-menu";
import ProductNoMediaCard from "./product-no-media-card";
import ProductSkeletonCard from "./product-skeleton-card";
import MarketplaceProductDetailsSkeleton from "./marketplace-product-details-skeleton";
import FilterPanelSkeleton from "./filter-panel-skeleton";
import MarketplaceSort from "./marketplace-sort";
import FloatingHelper from "../store/floating-helper";

const MarketPlace = (props) => {
  const { storePath, searchTerm, setSearchTerm, urlData } = props;
  const [sauce, setSauce] = useState("home");
  const [error, setError] = useState(null);
  const [hasTyped, setHasTyped] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [isLoadingSearchTerm, setIsLoadingSearchTerm] = useState(false);
  const [mobileActiveCategoryId, setMobileActiveCategoryId] = useState(null);
  const [isShowingFloater, setIsShowingFloater] = useState(false);
  const [currency, setCurrency] = useState("NGN");

  const [prevSearchTerm, setPrevSearchTerm] = useState(null);

  const [selectedFilterColors, setSelectedFilterColors] = useState([]);
  const [selectedFilterCategory, setSelectedFilterCategory] = useState(null);
  const [selectedFilterCountry, setSelectedFilterCountry] = useState(null);
  const [selectedFilterSizes, setSelectedFilterSizes] = useState([]);
  const [selectedFilterPrice, setSelectedFilterPrice] = useState({
    min: null,
    max: null,
  });

  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [sortOrder, setSortOrder] = useState("relevance");

  const dispatch = useAppDispatch();
  const { formatCurrency } = useFormatCurrency();

  const { token } = useToken();
  const client = useClient();
  const { navigateTo } = useHistoryManager(storePath);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const category = extractInfoFromUrl("category");
    if (category) {
      setSelectedFilterCategory(category);
    }

    const country = extractInfoFromUrl("country");
    if (country) {
      setSelectedFilterCountry(country);
    }

    const colors = extractInfoFromUrl("colors");
    if (colors) {
      setSelectedFilterColors(colors.split(",").filter(Boolean));
    }

    const sizes = extractInfoFromUrl("sizes");
    if (sizes && sizes.trim() !== "") {
      setSelectedFilterSizes(
        sizes
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      );
    } else {
      setSelectedFilterSizes([]); // 🔥
    }

    const minPrice = extractInfoFromUrl("minPrice");
    const maxPrice = extractInfoFromUrl("maxPrice");

    if (minPrice || maxPrice) {
      setSelectedFilterPrice({
        min: minPrice ? Number(minPrice) : null,
        max: maxPrice ? Number(maxPrice) : null,
      });
    }
  }, []);

  const [state, setState] = useState({
    products: [],
    limit: 20,
    total: 0,
    currentPage: 1,
    page: 1,
    randomSeed: null,
    search: "",
  });

  const queryKey = useMemo(
    () => [
      "feeds",
      state.limit,
      state.page,
      query,
      currency,
      selectedFilterColors,
      selectedFilterPrice?.min,
      selectedFilterPrice?.max,
      selectedFilterSizes,
      selectedFilterCategory,
      selectedFilterCountry,
      sortOrder,
      state.page > 1 ? state.randomSeed : null,
    ],
    [
      state.limit,
      state.page,
      query,
      currency,
      selectedFilterColors,
      selectedFilterPrice?.min,
      selectedFilterPrice?.max,
      selectedFilterSizes,
      selectedFilterCategory,
      selectedFilterCountry,
      sortOrder,
      state.page > 1 ? state.randomSeed : null,
    ],
  );
  const {
    data: feeds,
    isLoading,
    isFetching,
    refetch,
    isRefetching,
  } = useQuery(
    queryKey,
    async () => {
      const queryParams = [];

      if (query) {
        queryParams.push(`searchTerm=${query}`);
      }
      queryParams.push(`random=true`);
      queryParams.push(`withMedia=true`);
      queryParams.push(`removeOutOfStock=true`);
      queryParams.push(`currency=${currency}`);
      queryParams.push(`limit=${state.limit}`);
      queryParams.push(`page=${state.page}`);
      if (state.page > 1 && state.randomSeed) {
        queryParams.push(`randomSeed=${state.randomSeed}`);
      }
      if (selectedFilterColors.length) {
        queryParams.push(`color=${selectedFilterColors.join(",")}`);
      }

      if (selectedFilterSizes.length) {
        queryParams.push(`size=${selectedFilterSizes.join(",")}`);
      }
      if (
        selectedFilterPrice.min !== null &&
        selectedFilterPrice.max !== null
      ) {
        queryParams.push(`minPrice=${selectedFilterPrice.min}`);
        queryParams.push(`maxPrice=${selectedFilterPrice.max}`);
      }

      if (selectedFilterCategory) {
        queryParams.push(`category=${selectedFilterCategory}`);
      }

      if (selectedFilterCountry) {
        queryParams.push(`country=${selectedFilterCountry}`);
      }

      if (sortOrder && (sortOrder === "asc" || sortOrder === "desc")) {
        queryParams.push(`sortBy=amount`);
        queryParams.push(`orderBy=${sortOrder}`);
      }

      const params = queryParams.length ? `?${queryParams.join("&")}` : "";
      const res = await client(`product/products${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    },
    {
      enabled: Boolean(token),
      keepPreviousData: true,
      onError: (err) => {
        setError(err.message);
        console.error("Failed to fetch products:", err);
      },
    },
  );

  const { data: _categories = [], isLoading: isCategoryLoading } = useQuery(
    ["_categories", token],
    async () => {
      const res = await client(
        `configuration/public/category/product/categories`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return res.data;
    },
  );

  /* ---------------- FETCH FILTER OPTIONS ---------------- */

  const queryKeyFilter = useMemo(
    () => ["filter", searchTerm, currency],
    [searchTerm, currency],
  );
  const { data: filterData, isLoading: isLoadingFilterData } = useQuery(
    queryKeyFilter,
    async () => {
      const queryParams = [];
      queryParams.push(`currency=${currency}`);
      if (searchTerm) queryParams.push(`searchTerm=${searchTerm}`);
      const params = queryParams.length ? `?${queryParams.join("&")}` : "";

      const res = await client(`product/search/filters${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return res.data;
    },
    { enabled: Boolean(token) },
  );

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 750);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    dispatch(setAccessPointId(null));
    dispatch(setAccessPoint(null));
    dispatch(setAccessPoints([]));
    dispatch(setOrganisation(null));
    dispatch(setActiveCategory(null));
    dispatch(setCategories(null));
  }, []);

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      products: [],
      page: 1,
      randomSeed: null,
    }));
  }, [
    selectedFilterCategory,
    selectedFilterColors,
    selectedFilterSizes,
    selectedFilterCountry,
    selectedFilterPrice?.min,
    selectedFilterPrice?.max,
    sortOrder,
    query,
  ]);

  useEffect(() => {
    const q = extractInfoFromUrl("q");
    if (!q) return;
    setSearchTerm(q);
  }, []);

  useEffect(() => {
    if (searchTerm.trim() !== "") {
      setHasTyped(true);
    }

    const delayDebounce = setTimeout(() => {
      activateSearch();
    }, 1000); // wait 300ms after typing stops

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  const resetPage = () => {
    // if (resetCategory) dispatch(setActiveCategory(""));
    // if (persistQuery) storeInState();
    setState((prev) => ({
      ...prev,
      products: [],
      total: 0,
      currentPage: 1,
      page: 1,
      randomSeed: null,
    }));
  };

  const activateSearch = (value = null) => {
    const rawValue = value !== null ? value : searchTerm || "";
    const trimmed = rawValue.trim();
    if (query.trim() == trimmed) return;

    // Reset pagination state before searching
    resetPage(false, false);

    setIsLoadingSearchTerm(true);

    // Store the active query
    setQuery(trimmed);

    // const urlQuery = buildQueryString({ q: trimmed, currency });
    const urlQuery = buildQueryString({ q: trimmed });
    navigateTo(`${window.location.pathname}${urlQuery}`, { replace: true });
  };

  useEffect(() => {
    const q = extractInfoFromUrl("q");
    if (!q) return;
    setSearchTerm(q);
  }, []);

  const ProductPrice = ({
    product,
    accessPoint = null,
    recievedPriceOption = null,
  }) => {
    const { productPrice, getDisplayPrice } = useProductPriceDisplay(
      accessPoint,
      product,
      recievedPriceOption,
    );

    const amount =
      productPrice ?? getDisplayPrice(product) ?? product?.amount ?? 0;
    const formatted = formatCurrency(amount, product.currency);

    return (
      <h4
        className={styles.priceLine}
        style={{ fontSize: "1.9rem", fontWeight: 500 }}
      >
        {formatted}
      </h4>
    );
  };

  useEffect(() => {
    if (feeds && feeds.products) {
      const { products, limit, total, currentPage, randomSeed } = feeds;
      setState((prev) => {
        const all = [...(prev.products || []), ...products];
        const unique = Array.from(
          new Map(all.map((p) => [p.productId, p])).values(),
        );
        return {
          ...prev,
          products: unique,
          limit,
          total,
          currentPage,
          randomSeed: prev.page === 1 ? (randomSeed ?? null) : prev.randomSeed,
        };
      });
      setIsLoadingSearchTerm(false);
    }
  }, [feeds]);

  useEffect(() => {
    if (searchTerm && searchTerm.trim() !== "") {
      setSauce("search");
    } else {
      setSauce("home");
    }
  }, [searchTerm]);

  const openPage = (page) => {
    setState((prev) => ({ ...prev, page }));
  };

  const {
    startSwitch,
    isSwitching: hookSwitching,
    error: switchError,
  } = useSwitchingOrgs({ token });

  //   const hookSwitching = true;

  const handleProductClick = (product) => {
    setSearchTerm("");
    startSwitch(product);
  };
  const handleOpenCategoryModal = (category) => {
    setSelectedCategoryId(category.categoryId);
    openModal("categories");
  };

  const { modal, openModal, closeModal, modalState, modalBackButton } =
    useModalManager();
  useEffect(() => {
    if (prevSearchTerm !== null && prevSearchTerm !== "" && searchTerm === "") {
      // User cleared search

      setSelectedFilterColors([]);
      setSelectedFilterSizes([]);
      setSelectedFilterCategory(null);
      setSelectedFilterCountry(null);
      setSelectedFilterPrice({ min: null, max: null });

      const urlQuery = buildQueryString({
        colors: null,
        sizes: null,
        category: null,
        country: null,
        minPrice: null,
        maxPrice: null,
        q: null,
      });

      navigateTo(`${window.location.pathname}${urlQuery}`, {
        replace: true,
      });

      setState((prev) => ({
        ...prev,
        products: [],
        page: 1,
        randomSeed: null,
      }));
    }

    setPrevSearchTerm(searchTerm);
  }, [searchTerm]);

  return (
    <StoreBox
      {...props}
      isSwitching={false}
      hasTyped={hasTyped}
      setHasTyped={setHasTyped}
      inMainMarketplace={true}
    >
      <>
        {!hookSwitching && (
          <>
            <div className={styles.marketPlaceHeader}>
              {searchTerm ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                    width: "100%",
                  }}
                >
                  <BreadcrumbMenu sauce={sauce} setSearchTerm={setSearchTerm} />
                  <div className={styles.searchHeader}>
                    <h1>Search results - {searchTerm}</h1>

                    {isMobile && (
                      <div className={styles.mobileActions}>
                        {state.products.length !== 0 && (
                          <button
                            onClick={() => setShowFilterDrawer(true)}
                            className={styles.mobileFilterButton}
                          >
                            Filters
                          </button>
                        )}
                        <MarketplaceSort
                          searchTerm={searchTerm}
                          sortOrder={sortOrder}
                          setSortOrder={setSortOrder}
                          isMobile={isMobile}
                        />
                      </div>
                    )}

                    {!isMobile && (
                      <MarketplaceSort
                        searchTerm={searchTerm}
                        sortOrder={sortOrder}
                        setSortOrder={setSortOrder}
                        isMobile={false}
                      />
                    )}
                  </div>
                </div>
              ) : (
                <h1>Welcome to Circleplug Marketplace</h1>
              )}
            </div>

            {/* MOBILE CATEGORY BUBBLES */}
            {isMobile && !searchTerm && _categories?.categories?.length > 0 && (
              <div className={styles.mobileCategoryWrapper}>
                {/* MAIN CATEGORY SCROLL */}
                <div className={styles.mobileCategoryScroll}>
                  {_categories.categories.map((cat) => (
                    <div
                      key={cat.categoryId}
                      className={`${styles.mobileCategoryBubble} ${
                        mobileActiveCategoryId === cat.categoryId
                          ? styles.mobileActiveBubble
                          : ""
                      }`}
                      onClick={() =>
                        setMobileActiveCategoryId(
                          mobileActiveCategoryId === cat.categoryId
                            ? null
                            : cat.categoryId,
                        )
                      }
                    >
                      {cat.name}
                    </div>
                  ))}
                </div>

                {/* SUBCATEGORY SCROLL */}
                {mobileActiveCategoryId && (
                  <div className={styles.mobileSubCategoryScroll}>
                    {_categories.categories
                      .find((c) => c.categoryId === mobileActiveCategoryId)
                      ?.SubCategory?.map((sub) => (
                        <div
                          key={sub._id}
                          className={styles.mobileSubBubble}
                          onClick={() => {
                            setSearchTerm(sub.name);

                            setMobileActiveCategoryId(null);
                          }}
                        >
                          {sub.name}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}

            {!searchTerm &&
              !isMobile &&
              (isCategoryLoading || _categories?.categories?.length > 0) && (
                <div className={styles.marketPlaceTop}>
                  <div className={styles.marketPlaceCategories}>
                    {isCategoryLoading && (
                      <>
                        <div style={{ flex: 1.5 }}>
                          <Skeleton
                            height="100%"
                            width="100%"
                            style={{ borderRadius: "7px" }}
                          />
                        </div>

                        {Array.from({ length: 3 }).map((_, index) => (
                          <div key={index} style={{ flex: 1 }}>
                            <Skeleton
                              height="100%"
                              width="100%"
                              style={{ borderRadius: "7px" }}
                            />
                          </div>
                        ))}
                      </>
                    )}
                    {!isCategoryLoading &&
                      _categories?.categories?.length > 0 && (
                        <>
                          <div
                            className={styles.mainCategoriesList}
                            role="button"
                          >
                            {_categories.categories.map((mainCat) => (
                              <div
                                key={mainCat.categoryId}
                                className={styles.mainCategoriesItem}
                                onClick={() => handleOpenCategoryModal(mainCat)}
                              >
                                <span>{mainCat.name}</span>{" "}
                                <span>{rightArrowIcon}</span>
                              </div>
                            ))}
                          </div>

                          <div
                            style={{
                              flex: 2,
                              position: "relative",
                              overflow: "hidden", // prevents spill
                              width: "100%",
                              height: "100%",
                            }}
                          >
                            <MiniCarousel
                              categories={_categories.categories}
                              setSearchTerm={setSearchTerm}
                            />
                          </div>
                        </>
                      )}
                  </div>
                </div>
              )}
          </>
        )}

        <div
          className={styles.marketPlaceContainer}
          style={{
            display: searchTerm ? "flex" : "block",
            gap: searchTerm ? "2rem" : 0,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
              alignItems: "flex-start",
              maxWidth: "165rem",
              margin: "0 auto",
            }}
          >
            {searchTerm &&
              !isMobile &&
              (isLoadingFilterData ? (
                <FilterPanelSkeleton />
              ) : (
                filterData.productCategoryNames.length !== 0 && (
                  <ProductFilterPanel
                    setSelectedFilterColors={setSelectedFilterColors}
                    setSelectedFilterPrice={setSelectedFilterPrice}
                    setSelectedFilterSizes={setSelectedFilterSizes}
                    setSelectedFilterCategory={setSelectedFilterCategory}
                    setSelectedFilterCountry={setSelectedFilterCountry}
                    selectedFilterCategory={selectedFilterCategory}
                    selectedFilterCountry={selectedFilterCountry}
                    selectedFilterColors={selectedFilterColors}
                    selectedFilterSizes={selectedFilterSizes}
                    selectedFilterPrice={selectedFilterPrice}
                    filterData={filterData}
                  />
                )
              ))}

            <div className={styles.productsContainer}>
              {(isLoading || isFetching || isLoadingSearchTerm) &&
                state.products.length === 0 && (
                  <div className={styles.productsContainer}>
                    {Array.from({ length: 20 }).map((_, idx) => (
                      <ProductSkeletonCard
                        key={idx}
                        isSearchDesktop={Boolean(searchTerm && !isMobile)}
                      />
                    ))}
                  </div>
                )}

              {hookSwitching && <MarketplaceProductDetailsSkeleton />}

              {state.products.length === 0 &&
                !isLoading &&
                !error &&
                !isFetching && (
                  <div className={styles.marketPlaceContainerEmpty}>
                    <EmptyState>
                      There are no available products at this time. Check Back
                      later...
                    </EmptyState>
                  </div>
                )}

              {!hookSwitching &&
                !isLoading &&
                state.products.map((product) => {
                  const hasMedia =
                    Array.isArray(product.media) && product.media.length > 0;
                  const media = product.media
                    ?.map((m) => m)
                    .filter(Boolean)
                    .map((m) => ({
                      mediaUrl: m.mediaUrl,
                      mediaThumb: m.mediaThumb,
                      mediaName: m.mediaName,
                      mediaType: m.mediaType,
                      mediaExt: m.mediaExt,
                      mediaWidth: m.mediaWidth,
                      mediaHeight: m.mediaHeight,
                      mediaTime: m.mediaTime,
                      _id: m._id,
                    }));

                  return (
                    <div
                      key={product.productId}
                      data-product-id={product.productId}
                      onClick={() => handleProductClick(product)}
                      className={`${styles.productItem} ${searchTerm && !isMobile ? styles.productItemSearch : ""}`}
                      tabIndex={0}
                      role="button"
                    >
                      {hasMedia ? (
                        <div>
                          <Carousel
                            media={media}
                            enablePreview={false}
                            showIndicator={false}
                            customHeight={"20rem"}
                            galleryWidth={325}
                            hideThumb={true}
                            autoscroll={true}
                            inMainMarketPlace={true}
                            galleryContainerClassName="market-place-gallery"
                          />
                        </div>
                      ) : (
                        <ProductNoMediaCard
                          product={product}
                          searchTerm={searchTerm}
                          isMobile={isMobile}
                        />
                      )}

                      <div className={styles.itemInfo}>
                        <h4>
                          <p className={styles.names}>{product.name}</p>
                        </h4>

                        <ProductPrice
                          product={product}
                          accessPoint={null}
                          recievedPriceOption={product?.storeFrontDisplayPrice}
                        />

                        <h4>
                          <span>Sold by</span>{" "}
                          <p className={styles.names}>{product.host.name}</p>
                        </h4>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
          {!hookSwitching && (
            <Pagination
              infiniteScroll
              total={state.total}
              limit={state.limit}
              page={state.page}
              onPageClick={openPage}
              enableManualMode
              autoLoadThreshold={5}
              isLoading={isLoading}
              isFetching={isFetching}
              showEndOfList
              endOfListText="🎉 You've reached the end!"
            />
          )}
          <FloatingHelper
            modal={modal}
            setIsShowingFloater={setIsShowingFloater}
          />
        </div>

        {modal && modalState === "categories" && (
          <MarketPlaceCategories
            closeModal={closeModal}
            modalBackButton={modalBackButton}
            categories={_categories.categories}
            setSearchTerm={setSearchTerm}
            initialActiveCategoryId={selectedCategoryId}
          />
        )}

        {isMobile &&
          filterData?.productCategoryNames?.length !== 0 &&
          showFilterDrawer && (
            <Drawermenu
              showMenu={showFilterDrawer}
              setShowMenu={setShowFilterDrawer}
            >
              {({ closeMenu }) => (
                <div className={styles.marketplaceDrawerCase}>
                  <div className={styles.marketplaceDrawerHeader}>
                    <h3>Search Filters</h3>
                    <span className={styles.closeButton} onClick={closeMenu}>
                      {closeIcon}
                    </span>
                  </div>

                  <div className={styles.marketplaceDrawerContent}>
                    <ProductFilterPanel
                      setSelectedFilterColors={setSelectedFilterColors}
                      setSelectedFilterPrice={setSelectedFilterPrice}
                      setSelectedFilterSizes={setSelectedFilterSizes}
                      setSelectedFilterCategory={setSelectedFilterCategory}
                      setSelectedFilterCountry={setSelectedFilterCountry}
                      selectedFilterCategory={selectedFilterCategory}
                      selectedFilterCountry={selectedFilterCountry}
                      selectedFilterColors={selectedFilterColors}
                      selectedFilterSizes={selectedFilterSizes}
                      selectedFilterPrice={selectedFilterPrice}
                      filterData={filterData}
                      searchTerm={searchTerm}
                    />
                  </div>
                  <div className={styles.drawerFooter}>
                    <button className={styles.applyButton} onClick={closeMenu}>
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </Drawermenu>
          )}
      </>
    </StoreBox>
  );
};

export default MarketPlace;
